// Pedigree config (validatePedigreeConfig output) → TokenisedPedigree: a clean
// internal model for the detectors. Pure; no I/O, no React.
//
// Input members carry: pid, gender, fatherId, motherId, spouseIds[],
// proBandId, dob/dod, diagnoses[], genotypes[], phenotypes[]. childrenIds /
// siblingIds are NOT populated by validatePedigreeConfig, so we derive them
// from fatherId/motherId here. "Affected by disease D" = a diagnosis with
// id === D and displayOnPedigree === true. When conditionCarrierEnabled is true
// (FEATURE_CARRIER_STATUS_IN_CONDITION) the per-condition affectedStatus overlay
// is honoured: a displayed condition whose explicit affectedStatus is set but is
// not 'affected' (clinician downgrade to carrier/unaffected/unknown) is excluded
// from the analysis affected-set, keeping it aligned with the rendered
// symbol/legend. Flag off → affectedStatus is never consulted (byte-identical).

import { toSex } from './constants';
import { structureHash } from './structureHash';
import { getPersonAge } from 'src/util/util';

const PATHOGENIC = /pathogenic/i;
const LIKELY_PATH = /likely[ _-]?pathogenic/i;
const BENIGN = /benign/i;
const VUS = /uncertain|vus/i;

function pathogenicityClass(value) {
  const s = String(value || '').trim();
  if (!s) return 'unknown';
  if (LIKELY_PATH.test(s) || PATHOGENIC.test(s)) return 'pathogenic';
  if (BENIGN.test(s)) return 'benign';
  if (VUS.test(s)) return 'vus';
  return 'unknown';
}

function normaliseGenotype(g) {
  const gene = String((g && (g.symbol || g.gene || g.name)) || '')
    .split(' (')[0]
    .trim();
  const variants = Array.isArray(g && g.variants) ? g.variants : [];
  let strongest = 'unknown';
  let clinvarConflict = false;
  for (const v of variants) {
    const lab = pathogenicityClass(
      v.labInterpretation || v.clinicianInterpretation
    );
    const cv = pathogenicityClass(v.clinvarInterpretation);
    // Conflict = lab and ClinVar both classify but disagree on pathogenic vs benign.
    if (
      lab !== 'unknown' &&
      cv !== 'unknown' &&
      lab !== cv &&
      (lab === 'pathogenic' || lab === 'benign') &&
      (cv === 'pathogenic' || cv === 'benign')
    ) {
      clinvarConflict = true;
    }
    for (const cls of [lab, cv]) {
      if (cls === 'pathogenic') strongest = 'pathogenic';
      else if (cls === 'vus' && strongest === 'unknown') strongest = 'vus';
      else if (cls === 'benign' && strongest === 'unknown')
        strongest = 'benign';
    }
  }
  return {
    gene,
    carrierStatus: String((g && g.carrierStatus) || '').trim() || null,
    interpretation: strongest,
    clinvarConflict
  };
}

function isDeceased(m) {
  return Boolean(m.dod) || m.isAlive === 'No' || m.alive === 'No';
}

function tokenLabel(m, indexByPid) {
  if (m.proBandId) return 'PROBAND';
  const rel = (m.relationship || '').trim();
  if (rel) return rel.replace(/\s+/g, '_').toUpperCase();
  return `IND_${indexByPid.get(String(m.pid))}`;
}

// Ancestor set (pids) of an individual, walking parent edges.
function ancestorsOf(pid, byPid) {
  const out = new Set();
  const stack = [pid];
  while (stack.length) {
    const cur = byPid.get(stack.pop());
    if (!cur) continue;
    for (const p of [cur.fatherId, cur.motherId]) {
      if (p && byPid.has(p) && !out.has(p)) {
        out.add(p);
        stack.push(p);
      }
    }
  }
  return out;
}

// A consanguinity loop = a mating pair that shares an ancestor. F is left null
// (a precise inbreeding coefficient is out of scope for v1).
function detectConsanguinity(individuals, byPid) {
  const loops = [];
  const seen = new Set();
  for (const ind of individuals) {
    for (const spouseId of ind.spouseIds) {
      const key = [ind.pid, spouseId].sort().join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      const a = ancestorsOf(ind.pid, byPid);
      const b = ancestorsOf(spouseId, byPid);
      const shared = [...a].some((x) => b.has(x));
      if (shared)
        loops.push({ partners: [ind.pid, spouseId], coefficientF: null });
    }
  }
  return loops;
}

export function tokenisePedigree(
  members = [],
  { carrierStatusByMember = {}, conditionCarrierEnabled = false } = {}
) {
  const nodes = (members || []).filter((m) => m && m.pid != null);

  // Stable index for fallback tokens (sorted by pid string → order-independent).
  const indexByPid = new Map(
    [...nodes]
      .map((m) => String(m.pid))
      .sort()
      .map((pid, i) => [pid, i + 1])
  );

  const familyDiseases = new Map(); // diseaseId -> { id, name }
  const individuals = nodes.map((m) => {
    const affectedDiseases = new Set();
    for (const d of m.diagnoses || []) {
      if (d && d.id && d.displayOnPedigree === true) {
        // Flag-gated downgrade: an explicit affectedStatus that is set but not
        // 'affected' removes the condition from the affected-set (carrier etc.).
        const downgraded =
          conditionCarrierEnabled &&
          d.affectedStatus &&
          d.affectedStatus !== 'affected';
        if (!downgraded) affectedDiseases.add(d.id);
        if (!familyDiseases.has(d.id))
          familyDiseases.set(d.id, { id: d.id, name: d.name });
      }
    }
    return {
      pid: String(m.pid),
      token: tokenLabel(m, indexByPid),
      isProband: Boolean(m.proBandId),
      sex: toSex(m.gender),
      isDummy: Boolean(m.isDummy),
      affectedDiseases,
      affected: affectedDiseases.size > 0,
      deceased: isDeceased(m),
      ageAtAssessment: (() => {
        try {
          const a = getPersonAge(m);
          return Number.isFinite(a) ? a : null;
        } catch (_) {
          return null;
        }
      })(),
      phenotypes: (m.phenotypes || [])
        .filter((p) => p && p.id && p.observed !== false)
        .map((p) => p.id),
      genotypes: (m.genotypes || []).map(normaliseGenotype),
      carrierByDisease: carrierStatusByMember[String(m.pid)] || {},
      fatherId: m.fatherId != null ? String(m.fatherId) : null,
      motherId: m.motherId != null ? String(m.motherId) : null,
      spouseIds: (Array.isArray(m.spouseIds) ? m.spouseIds : []).map(String),
      childrenIds: [],
      siblingIds: []
    };
  });

  const byPid = new Map(individuals.map((i) => [i.pid, i]));

  // Derive children from parent edges.
  for (const ind of individuals) {
    for (const parentId of [ind.fatherId, ind.motherId]) {
      const parent = parentId && byPid.get(parentId);
      if (parent && !parent.childrenIds.includes(ind.pid))
        parent.childrenIds.push(ind.pid);
    }
  }
  // Siblings = share at least one parent (excluding self).
  for (const ind of individuals) {
    const sibs = new Set();
    for (const parentId of [ind.fatherId, ind.motherId]) {
      const parent = parentId && byPid.get(parentId);
      if (!parent) continue;
      for (const childId of parent.childrenIds)
        if (childId !== ind.pid) sibs.add(childId);
    }
    ind.siblingIds = [...sibs];
  }

  const proband =
    individuals.find((i) => i.isProband) || individuals[0] || null;

  return {
    proband: proband ? proband.pid : null,
    individuals,
    byPid,
    familyDiseases: [...familyDiseases.values()],
    consanguinityLoops: detectConsanguinity(individuals, byPid),
    structureHash: structureHash(members)
  };
}
